import moment from "moment";

import {
  arrayToOptions,
  isValidEmail,
  validateMobileNumber,
} from "utilities/generalUtility";
import K from "utilities/constants";

const { Prospect, Candidates, Offer, Hired, Terminated, Withdrew, Declined } =
  K.Status;

export const STATUS_LIST = [Prospect, Candidates, Offer, Hired,
  Terminated,
  Declined,
  Withdrew,
].map(({ id, name }) => ({ id, name }));

const SUB_STATUS_CONFIG = [
  { status: Prospect, subStatuses: [Prospect.subStatus.Sourced] },
  { status: Candidates, subStatuses: [Candidates.subStatus.NeedsReview] },
  { status: Offer, subStatuses: [Offer.subStatus.Approval] },
  { status: Hired, subStatuses: [Hired.subStatus.StartedWork] },
  {
    status: Terminated,
    subStatuses: Object.values(Terminated.subStatus).map(({ id, name }) => ({
      id,
      name,
    })),
  },
  {
    status: Declined,
    subStatuses: Object.values(Declined.subStatus).map(({ id, name }) => ({
      id,
      name,
    })),
  },
  {
    status: Withdrew,
    subStatuses: Object.values(Withdrew.subStatus).map(({ id, name }) => ({
      id,
      name,
    })),
  },
];

export const SUB_STATUS_LIST = SUB_STATUS_CONFIG.flatMap(
  ({ status, subStatuses }) =>
    subStatuses.map(({ id, name }) => ({ id, name, statusId: status.id })),
);

export const SUB_STATUS_MAP = Object.fromEntries(
  SUB_STATUS_CONFIG.map(({ status, subStatuses }) => [
    status.id,
    subStatuses.map(({ id, name }) => ({ id, name })),
  ]),
);

export const HIRE_TYPES = arrayToOptions(K.CandidateType.getAll());

export const DATE_FIELDS = [
  "Sourced Date",
  "Application Date",
  "Date Phone Screen Complete",
  "Date Submitted",
  "Interview Date",
  "Date Interview Feedback Received",
  "Date Offer Approval",
  "Date Offer Extended",
  "Date Offer Accepted",
  "Hire Date",
  "Start Date",
  "Termination Date",
  "Outside Retention Period",
  "Declined Date",
  "Offer Rescinded Date",
  "Withdrew Date",
  "Unresponsive Date",
];

export const REQUIRED_FIELDS = [
  "First Name",
  "Last Name",
  "Email",
  "Candidate Type",
];

export const RECRUITER_KEYS = ["First Recruiter ID", "Second Recruiter ID"];

const requiredDatesForFinalStatus = (
  status,
  statusDate,
  extraDateBySubStatusId = {},
) =>
  Object.fromEntries(
    Object.values(status.subStatus).map(({ id }) => [
      id,
      extraDateBySubStatusId[id]
        ? [statusDate, extraDateBySubStatusId[id]]
        : [statusDate],
    ]),
  );

const REQUIRED_DATES_BY_SUB_STATUS = {
  [Prospect.subStatus.Sourced.id]: ["Sourced Date"],
  [Candidates.subStatus.NeedsReview.id]: ["Application Date"],
  [Offer.subStatus.Approval.id]: [
    "Application Date",
    "Date Phone Screen Complete",
    "Date Submitted",
    "Interview Date",
    "Date Interview Feedback Received",
    "Date Offer Approval",
  ],
  [Hired.subStatus.StartedWork.id]: [
    "Application Date",
    "Date Phone Screen Complete",
    "Date Submitted",
    "Interview Date",
    "Date Interview Feedback Received",
    "Date Offer Approval",
    "Date Offer Extended",
    "Date Offer Accepted",
    "Hire Date",
    "Start Date",
  ],
  [Terminated.subStatus.NoShowToDay1.id]: [
    "Application Date",
    "Date Phone Screen Complete",
    "Date Submitted",
    "Interview Date",
    "Date Interview Feedback Received",
    "Date Offer Extended",
    "Date Offer Accepted",
    "Hire Date",
    "Start Date",
    "Termination Date",
  ],
  ...requiredDatesForFinalStatus(Declined, "Declined Date", {
    [Declined.subStatus.OfferRescinded.id]: "Offer Rescinded Date",
  }),
  ...requiredDatesForFinalStatus(Withdrew, "Withdrew Date", {
    [Withdrew.subStatus.Unresponsive.id]: "Unresponsive Date",
  }),
};

export const ERROR_CLASS = "cell-error";
const STATUS_CHANGE_WARNING_MESSAGES = {
  terminated: "The following candidates will be terminated",
  declined: "The following candidates will be declined",
  withdrew: "The following candidates will be withdrawn",
  offer_approval: "The following candidates will be moved to Offer Approval",
};

export const getStatusChangeWarningMessage = (key) =>
  STATUS_CHANGE_WARNING_MESSAGES[key] ??
  "The following candidates will change status";

export const isValidDate = (value) =>
  moment(value, K.DateFormat.USFormat, true).isValid();

export const isValidSelect = (items, id, idKey = "id") =>
  !id || items.some((item) => `${item[idKey]}` === `${id}`);

const resolveItems = (items, data) =>
  typeof items === "function" ? items(data) : items;

export const getAllowedSubStatuses = (data) =>
  SUB_STATUS_MAP[data?.["Status ID"]] ?? [];

export const resolveLabel = (items, id, labelKey = "name", idKey = "id") =>
  items.find((item) => `${item[idKey]}` === `${id}`)?.[labelKey] ?? "";

export const getRawValue = (data, dataKey, headerName) =>
  data[dataKey] ?? data[headerName];

export const getRequiredDates = (row) => {
  const subStatus = SUB_STATUS_LIST.find(
    (item) => `${item.id}` === `${row["Sub Status ID"]}`,
  );
  return REQUIRED_DATES_BY_SUB_STATUS[subStatus?.id] ?? [];
};

export const isDateRequired = (row, field) =>
  getRequiredDates(row).includes(field);

export const isDuplicateRecruiter = (data, dataKey) => {
  const raw = data[dataKey];
  if (!raw) return false;
  return RECRUITER_KEYS.some(
    (key) => key !== dataKey && data[key] && `${data[key]}` === `${raw}`,
  );
};

export const parseAndNormalizeDateValue = (value) => {
  const raw = `${value ?? ""}`.trim();
  if (!raw) return { value: "", isValid: true };

  const parsed = moment(
    raw,
    [
      K.DateFormat.DashUSFormat,
      K.DateFormat.USFormat,
      K.DateFormat.ShortUSFormat,
      K.DateFormat.Response,
      "MM-DD-YY",
    ],
    true,
  );
  if (!parsed.isValid()) return { value: raw, isValid: false };

  return { value: parsed.format(K.DateFormat.USFormat), isValid: true };
};

export const formatDateValue = (value) => {
  const { value: normalized, isValid } = parseAndNormalizeDateValue(value);
  if (!normalized || !isValid) return normalized;
  return moment(normalized, K.DateFormat.USFormat).format(
    K.DateFormat.DashUSFormat,
  );
};

export const formatPhoneNumber = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value;
};

export const recruiterFormatValue = (items, value) => {
  const found = items.find((item) => item.id === +value);
  if (!found) return "";
  const clientNames = found.clients?.map((c) => c.name).join(", ");
  return `${found.name} - ${clientNames || "N/A"}`;
};

export const validateEmail = (params) => {
  const { value, api } = params;
  if (!value) return null;
  if (!isValidEmail(value)) return "Enter a valid email format!";
  const normalizedEmail = value.toLowerCase();
  let count = 0;
  api.forEachNode((node) => {
    if (node.data.Email?.toLowerCase() === normalizedEmail) count += 1;
  });
  if (count > 1) return "Duplicate email address";
  return null;
};

export const toSelectParams = (items, labelKey = "name", idKey = "id") => ({
  values: items.map((item) => `${item[idKey]}`),
  formatValue: (value) => resolveLabel(items, value, labelKey, idKey),
});

export const editableField = (headerName, dataKey = headerName) => ({
  headerName,
  field: headerName,
  editable: true,
  tooltipValueGetter: ({ value }) => value || null,
  valueGetter: (params) =>
    params.data[dataKey] ?? params.data[headerName] ?? "",
  valueSetter: (params) => {
    params.node.setData({ ...params.data, [dataKey]: params.newValue });
    return true;
  },
});

export const requiredField = (message) => ({
  cellClass: ({ value }) => (!value ? ERROR_CLASS : null),
  tooltipValueGetter: ({ value }) => (!value ? message : value),
});

export const validatedField = (validate, message) => ({
  cellClass: ({ value }) => (value && !validate(value) ? ERROR_CLASS : null),
  tooltipValueGetter: ({ value }) => {
    if (value && !validate(value)) return message;
    return value;
  },
});

export const selectDisplayValue = (
  data,
  dataKey,
  headerName,
  items,
  labelKey,
  idKey,
) => {
  const raw = getRawValue(data, dataKey, headerName);
  return (
    resolveLabel(resolveItems(items, data), raw, labelKey, idKey) || raw || ""
  );
};

export const selectTooltipValueGetter =
  (
    dataKey,
    headerName,
    items,
    { labelKey = "name", idKey = "id", required } = {},
  ) =>
  ({ data }) => {
    const raw = getRawValue(data, dataKey, headerName);
    const resolvedItems = resolveItems(items, data);
    if (!raw && required) return `${headerName} is required`;
    if (raw && !isValidSelect(resolvedItems, raw, idKey))
      return "Invalid value";
    return resolveLabel(resolvedItems, raw, labelKey, idKey) || null;
  };

export const selectCellClass =
  (dataKey, headerName, items, { idKey = "id", required } = {}) =>
  ({ data }) => {
    const raw = getRawValue(data, dataKey, headerName);
    const resolvedItems = resolveItems(items, data);
    if (
      (!raw && required) ||
      (raw && !isValidSelect(resolvedItems, raw, idKey))
    )
      return ERROR_CLASS;
    return null;
  };

export const recruiterEditorParams = (dataKey, recruiters) => (params) => {
  const otherValues = RECRUITER_KEYS.filter((k) => k !== dataKey)
    .map((k) => params.data[k])
    .filter(Boolean);
  const available = recruiters.filter((r) => !otherValues.includes(`${r.id}`));
  return {
    values: available.map((item) => `${item.id}`),
    formatValue: (value) => recruiterFormatValue(recruiters, value),
  };
};

export const recruiterCellClass =
  (dataKey, recruiters) =>
  ({ data }) => {
    const raw = getRawValue(data, dataKey, dataKey);
    if (raw && !isValidSelect(recruiters, raw)) return ERROR_CLASS;
    if (isDuplicateRecruiter(data, dataKey)) return ERROR_CLASS;
    return null;
  };

export const recruiterTooltipValueGetter =
  (dataKey, recruiters) =>
  ({ data }) => {
    const raw = getRawValue(data, dataKey, dataKey);
    if (raw && !isValidSelect(recruiters, raw)) return "Invalid value";
    if (isDuplicateRecruiter(data, dataKey))
      return "Recruiter is already assigned";
    return recruiterFormatValue(recruiters, raw) || null;
  };

export const dateValueGetter = (headerName) => (params) =>
  formatDateValue(params.data[headerName]);

export const dateValueSetter = (headerName) => (params) => {
  const { value: formatted } = parseAndNormalizeDateValue(params.newValue);
  params.node.setData({ ...params.data, [headerName]: formatted });
  return true;
};

export const dateTooltipValueGetter = (headerName) => (params) => {
  const raw = params.data[headerName];
  if (raw && !isValidDate(raw)) return "Invalid date";
  if (!raw && isDateRequired(params.data, headerName))
    return `${headerName} is required`;
  return formatDateValue(params.value) || null;
};

export const dateCellClass = (headerName) => (params) => {
  const raw = params.data[headerName];
  if (raw && !isValidDate(raw)) return ERROR_CLASS;
  if (!raw && isDateRequired(params.data, headerName)) return ERROR_CLASS;
  return null;
};

export const statusValueSetter = (params) => {
  const statusChanged = params.data["Status ID"] !== params.newValue;
  params.node.setData({
    ...params.data,
    "Status ID": params.newValue,
    ...(statusChanged && { "Sub Status ID": "" }),
  });
  return true;
};

export const subStatusEditorParams = (params) =>
  toSelectParams(getAllowedSubStatuses(params.data));

export const emailCellClass = (params) => {
  if (!params.value || validateEmail(params)) return ERROR_CLASS;
  return null;
};

export const emailTooltipValueGetter = (params) => {
  if (!params.value) return "Email is required";
  return validateEmail(params) || params.value;
};

const STATIC_SELECT_VALIDATIONS = [
  { dataKey: "Candidate Type", items: HIRE_TYPES, idKey: "value" },
  { dataKey: "Status ID", headerName: "Status", items: STATUS_LIST },
  {
    dataKey: "Sub Status ID",
    headerName: "Sub Status",
    items: getAllowedSubStatuses,
  },
];

export const hasValidationErrors = (rows) => {
  const emailCounts = {};
  rows.forEach((row) => {
    const email = row.Email?.toLowerCase();
    if (email) emailCounts[email] = (emailCounts[email] || 0) + 1;
  });

  return rows.some((row) => {
    if (REQUIRED_FIELDS.some((field) => !row[field])) return true;

    const email = row.Email;
    if (!isValidEmail(email) || emailCounts[email.toLowerCase()] > 1)
      return true;

    if (row["Mobile Number"] && !validateMobileNumber(row["Mobile Number"]))
      return true;

    if (
      STATIC_SELECT_VALIDATIONS.some(
        ({ dataKey, headerName, items, idKey = "id" }) => {
          const raw =
            row[dataKey] ?? (headerName ? row[headerName] : undefined);
          return raw && !isValidSelect(resolveItems(items, row), raw, idKey);
        },
      )
    )
      return true;

    if (RECRUITER_KEYS.some((key) => isDuplicateRecruiter(row, key)))
      return true;

    const requiredDates = getRequiredDates(row);
    if (requiredDates.some((field) => !row[field])) return true;

    return DATE_FIELDS.some((field) => row[field] && !isValidDate(row[field]));
  });
};
